"use client"

import Link from "next/link"
import { Card, CardContent } from "@/components/ui/card"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { HeroSection } from "@/components/ui/hero-section"

const SUPPORT_EMAIL = "support@whatgrassisthis.com"
const REQUEST_SUBJECT = "Delete my What Grass Is This account"
const REQUEST_BODY = [
  "Please delete my What Grass Is This account and data.",
  "",
  "Email address on the account:",
  "Sign-in method (Apple, Google, or email):",
].join("\n")
const REQUEST_HREF = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(REQUEST_SUBJECT)}&body=${encodeURIComponent(REQUEST_BODY)}`

const linkClass = "text-green-600 hover:text-green-700 font-semibold"

export function DeleteAccountContent() {
  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="relative px-4 pt-6 md:pt-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-2">
            <BreadcrumbComponent items={[{ label: "Home", href: "/" }, { label: "Delete Account" }]} />
          </div>

          <HeroSection
            size="minimal"
            showSparkle={false}
            showGlow={false}
            title="Delete Your Account and Data"
          />

          <Card className="bg-white/95 backdrop-blur-xs border-green-200 shadow-2xl">
            <CardContent className="p-4 sm:p-6 md:p-8 lg:p-12">
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 mb-8">
                  This page covers the What Grass Is This? app for iOS and Android and the whatgrassisthis.com
                  website. The developer is What Grass Is This?, and one account works across all of them.
                  Deleting it removes your data everywhere.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">Delete inside the app</h2>
                <ol className="list-decimal pl-6 mb-8 space-y-2 text-gray-700">
                  <li>Open What Grass Is This? and sign in.</li>
                  <li>Go to Settings, then Data &amp; Privacy.</li>
                  <li>Tap Delete account, then confirm.</li>
                </ol>
                <p className="text-gray-700 mb-8">
                  Deletion starts right away and cannot be undone. Export your data first from Data &amp; Privacy if
                  you want a copy.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">Delete without the app</h2>
                <p className="text-gray-700 mb-4">
                  If you no longer have the app installed, or cannot sign in, email us from the address on your
                  account.
                </p>
                <ol className="list-decimal pl-6 mb-4 space-y-2 text-gray-700">
                  <li>
                    Send an email to{" "}
                    <a href={REQUEST_HREF} className={linkClass}>
                      {SUPPORT_EMAIL}
                    </a>{" "}
                    with the subject &ldquo;{REQUEST_SUBJECT}&rdquo;.
                  </li>
                  <li>Tell us the email address on the account and how you sign in (Apple, Google, or email).</li>
                  <li>We reply to confirm it is your account, then delete it.</li>
                </ol>
                <p className="text-gray-700 mb-4">
                  We complete email requests within 30 days and confirm by reply when it is done.
                </p>
                <p className="text-gray-700 mb-8">
                  You can also email us to remove data the in-app button does not cover, such as a Community post
                  or a service quote request.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">What we delete</h2>
                <ul className="list-disc pl-6 mb-8 space-y-2 text-gray-700">
                  <li>Your account, sign-in details, and profile</li>
                  <li>Saved lawns, zones, and care plans</li>
                  <li>Journal entries and the photos attached to them</li>
                  <li>Scan and diagnosis history, feedback, and the lawn photos you uploaded for scans</li>
                  <li>Reminders, task completions, tool results, and badges</li>
                  <li>Lawn assistant chats saved to your account</li>
                  <li>Push notification tokens, notification settings, and alert history</li>
                  <li>Your newsletter subscription, if you used the same email address</li>
                  <li>Any active web subscription, which we cancel so it does not renew</li>
                  <li>Your Sign in with Apple authorization, when you signed in with Apple</li>
                </ul>
                <p className="text-gray-700 mb-8">
                  These are deleted when you confirm in the app, or when we process your email request. There is no
                  grace period and no way to restore them.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">What we keep</h2>
                <ul className="list-disc pl-6 mb-8 space-y-2 text-gray-700">
                  <li>
                    <strong>Subscription and purchase records.</strong> Google Play, the App Store, and Stripe keep
                    their own billing records under their own policies. Deleting your account does not cancel an app
                    store subscription. Cancel it in Google Play or Apple Subscriptions first, or it keeps renewing.
                  </li>
                  <li>
                    <strong>Anonymous assistant questions.</strong> Text typed into the lawn assistant is stored
                    with no account, device, or location identifier, so we cannot find or remove one person&apos;s
                    messages afterward.
                  </li>
                  <li>
                    <strong>Usage and crash analytics.</strong> Product analytics and crash reports are tied to a
                    random ID, not your name or email. Email us and we remove the events tied to your account within
                    30 days of the request.
                  </li>
                  <li>
                    <strong>Community posts and quote requests.</strong> These are kept until you ask us to remove
                    them by email. We remove them within 30 days of the request.
                  </li>
                  <li>
                    <strong>Legal and security records.</strong> We keep limited records when the law requires it or
                    when needed to stop abuse and fraud.
                  </li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">More information</h2>
                <p className="text-gray-700">
                  See the{" "}
                  <Link href="/privacy" className={linkClass}>
                    Privacy Policy
                  </Link>{" "}
                  for everything we collect and why. Questions about deletion go to{" "}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className={linkClass}>
                    {SUPPORT_EMAIL}
                  </a>
                  .
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
